import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Clock } from "lucide-react";
import { toast } from "sonner";
import { updateRecipeSchedule } from "@/lib/api";

const WEEKDAYS = [
  { id: 0, label: "Seg" },
  { id: 1, label: "Ter" },
  { id: 2, label: "Qua" },
  { id: 3, label: "Qui" },
  { id: 4, label: "Sex" },
  { id: 5, label: "Sáb" },
  { id: 6, label: "Dom" },
];

const INTERVALS = [
  { id: "15", label: "A cada 15 min" },
  { id: "30", label: "A cada 30 min" },
  { id: "45", label: "A cada 45 min" },
  { id: "60", label: "A cada 1 hora" },
  { id: "120", label: "A cada 2 horas" },
  { id: "180", label: "A cada 3 horas" },
  { id: "360", label: "A cada 6 horas" },
  { id: "720", label: "A cada 12 horas" },
  { id: "1440", label: "A cada 24 horas" },
];

export default function ScheduleDialog({ recipe, onUpdated, triggerLabel = "Agendar" }) {
  const [open, setOpen] = useState(false);
  const init = recipe.schedule || { enabled: false, weekdays: [0,1,2,3,4], start_time: "07:00", end_time: "18:00", interval_minutes: 60 };
  const [enabled, setEnabled] = useState(!!init.enabled);
  const [weekdays, setWeekdays] = useState(init.weekdays || [0,1,2,3,4]);
  const [startTime, setStartTime] = useState(init.start_time || "07:00");
  const [endTime, setEndTime] = useState(init.end_time || "18:00");
  const [interval, setInterval] = useState(String(init.interval_minutes || 60));
  const [saving, setSaving] = useState(false);

  // Re-sync local state with the latest recipe.schedule every time the dialog opens.
  // Without this, useState initialisers run only on mount → second edit keeps stale values.
  useEffect(() => {
    if (!open) return;
    const s = recipe.schedule || {};
    setEnabled(!!s.enabled);
    setWeekdays(Array.isArray(s.weekdays) && s.weekdays.length ? s.weekdays : [0,1,2,3,4]);
    setStartTime(s.start_time || "07:00");
    setEndTime(s.end_time || "18:00");
    setInterval(String(s.interval_minutes || 60));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recipe?.id, recipe?.schedule?.enabled, recipe?.schedule?.start_time, recipe?.schedule?.end_time, recipe?.schedule?.interval_minutes, JSON.stringify(recipe?.schedule?.weekdays || [])]);

  function toggleDay(d) {
    setWeekdays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort((a,b)=>a-b));
  }
  async function onSave() {
    if (enabled && weekdays.length === 0) return toast.error("Escolhe pelo menos um dia da semana");
    setSaving(true);
    try {
      await updateRecipeSchedule(recipe.id, {
        enabled, weekdays, start_time: startTime, end_time: endTime, interval_minutes: Number(interval)
      });
      toast.success(enabled ? "Agendamento ativado" : "Agendamento desativado");
      setOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="recipe-card-schedule-button">
          <Calendar className="h-3.5 w-3.5 mr-1"/> {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Agendar execuções</DialogTitle>
          <DialogDescription>Define janela horária, intervalo e dias da semana. O sistema executa a recipe automaticamente nas horas selecionadas.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Ativar agendamento</Label>
              <p className="text-xs text-muted-foreground">Liga/desliga sem perder as definições.</p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="schedule-enabled-switch"/>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs" htmlFor="start-time">Hora início</Label>
              <Input id="start-time" data-testid="schedule-start-time-input" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} disabled={!enabled}/>
            </div>
            <div>
              <Label className="text-xs" htmlFor="end-time">Hora fim</Label>
              <Input id="end-time" data-testid="schedule-end-time-input" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} disabled={!enabled}/>
            </div>
          </div>

          <div>
            <Label className="text-xs">Intervalo</Label>
            <Select value={interval} onValueChange={setInterval} disabled={!enabled}>
              <SelectTrigger data-testid="schedule-interval-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {INTERVALS.map(i => <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Dias da semana</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {WEEKDAYS.map(d => (
                <button key={d.id} type="button" onClick={() => toggleDay(d.id)} disabled={!enabled}
                        data-testid={`schedule-weekday-${d.id}`}
                        className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${weekdays.includes(d.id) ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-foreground border-border hover:bg-muted'} ${!enabled ? 'opacity-50' : ''}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {enabled && (
            <div className="text-xs text-muted-foreground rounded-lg bg-muted/40 p-3">
              <Clock className="inline h-3 w-3 mr-1"/>
              Resumo: das <span className="font-medium text-foreground">{startTime}</span> às <span className="font-medium text-foreground">{endTime}</span>, a cada <span className="font-medium text-foreground">{INTERVALS.find(i => i.id === interval)?.label?.toLowerCase().replace('a cada ','')}</span>, em {weekdays.map(w => WEEKDAYS.find(d => d.id===w)?.label).join(', ') || '—'}.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={onSave} disabled={saving} data-testid="schedule-save-button">{saving ? "A guardar…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
